/** A modest display boost for the painted castles and their cloth; the approved PNGs stay untouched. */
export const CITY_SATURATION = 1.28;
export const CITY_BRIGHTNESS = 1.12;
// The largest castle is 91 CSS px wide at s=110: 384 source pixels cover even the phone's 3x display.
const DISPLAY_MAX = 384;

/** Boost only RGB. Keeping every alpha byte preserves the painted edges and transparent padding. */
export function boostCityPixels(pixels: Uint8ClampedArray): void {
  for (let i = 0; i < pixels.length; i += 4) {
    if (pixels[i + 3] === 0) continue;
    const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2];
    const light = r * 0.2126 + g * 0.7152 + b * 0.0722;
    pixels[i] = (light + (r - light) * CITY_SATURATION) * CITY_BRIGHTNESS;
    pixels[i + 1] = (light + (g - light) * CITY_SATURATION) * CITY_BRIGHTNESS;
    pixels[i + 2] = (light + (b - light) * CITY_SATURATION) * CITY_BRIGHTNESS;
  }
}

/** Prepare once as the PNG loads; frames draw this cached copy. Canvas failure keeps the original art usable. */
export function prepareCityDisplay(image: HTMLImageElement): HTMLCanvasElement | null {
  const canvas = document.createElement("canvas");
  try {
    const scale = Math.min(1, DISPLAY_MAX / Math.max(image.naturalWidth, image.naturalHeight));
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("no city display context");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
    boostCityPixels(pixels.data);
    ctx.putImageData(pixels, 0, 0);
    return canvas;
  } catch {
    canvas.width = canvas.height = 0;
    return null;
  }
}
